// src/pages/SiteList.js - VERSION AVEC CHARTE GRAPHIQUE SECURECONTROL
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Options pour les filtres
const STATUT_SITE_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "1", label: "Actif" },
  { value: "0", label: "Inactif" }
];

const STATUT_SUPPRIME_OPTIONS = [
  { value: "0", label: "Sites actifs" },
  { value: "1", label: "Sites supprimés" }
];

// Fonction pour le label et couleur du statut
const statutSiteLabel = (statut) => {
  switch (statut) {
    case 1: return { text: "Actif", className: "badge bg-success", icon: "bi-check-circle" };
    case 0: return { text: "Inactif", className: "badge bg-secondary", icon: "bi-x-circle" };
    default: return { text: "Inconnu", className: "badge bg-light text-dark", icon: "bi-question-circle" };
  }
};

const SiteList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const clientIdParam = queryParams.get('client_id');
  
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [clients, setClients] = useState([]);
  
  // Récupérer les infos utilisateur
  const [userInfo, setUserInfo] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Filtres
  const [filters, setFilters] = useState({
    search: "",
    id_client: clientIdParam || "",
    statut_site: "",
    supprimer: "0", // Par défaut, n'affiche pas les supprimés
    page: 1,
    limit: 20
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Permission
  const userProfile = userInfo ? String(userInfo.profile || userInfo.poste || "") : "";
  const isGestionnaire = userProfile === "3";
  const isControleur = userProfile === "2";
  const canAccess = userProfile === "2" || userProfile === "3";

  // Charger la liste des clients pour le filtre
  useEffect(() => {
    const fetchClients = async () => {
      if (!userInfo) return;
      
      const token = localStorage.getItem("access_token");
      if (!token) return;
      
      try {
        const response = await axios.get(
          `${API_BASE_URL}/clients/list.php?limit=1000`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        
        if (response.data?.success) {
          setClients(response.data.data.clients || []);
        }
      } catch (err) {
        console.error("Erreur chargement clients:", err);
      }
    };
    
    if (userInfo) {
      fetchClients();
    }
  }, [userInfo]);

  const fetchSites = useCallback(async (page = 1) => {
    if (!userInfo) return;
    
    setLoading(true);
    setServerError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setServerError("Token d'authentification manquant.");
      setLoading(false);
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      
      Object.keys(filters).forEach(key => {
        if (key === 'page' || key === 'limit') return; // Gérés séparément
        if (filters[key] !== "" && filters[key] !== false && filters[key] !== null) {
          params.append(key, filters[key]);
        }
      });
      
      params.append("page", page);
      params.append("limit", filters.limit);
      
      const url = `${API_BASE_URL}/sites/list.php?${params.toString()}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      if (response.data?.success) {
        setSites(response.data.data || []);
        
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
        
        if ((response.data.data || []).length === 0) {
          setInfoMsg("Aucun site trouvé avec les filtres actuels.");
        } else {
          setInfoMsg("");
        }
      } else {
        setServerError(response.data?.message || "Erreur de chargement.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
      } else if (err.response?.status === 400) {
        setServerError(err.response.data?.message || "Erreur dans la requête.");
      } else if (!err.response) {
        setServerError("Serveur indisponible.");
      } else {
        setServerError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, navigate, userInfo]);

  useEffect(() => {
    if (userInfo) {
      fetchSites(filters.page);
    }
  }, [fetchSites, userInfo]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset à la première page lors du changement de filtre
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSites(1);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      id_client: "",
      statut_site: "",
      supprimer: "0",
      page: 1,
      limit: 20
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchSites(1);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Si pas d'utilisateur
  if (!userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des sites</h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des informations...</p>
          </div>
        </section>
      </Layout>
    );
  }

  // Si pas accès
  if (!canAccess) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des sites</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Sites</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="mb-4">
              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                width: '80px',
                height: '80px',
                backgroundColor: '#F5F7FA',
                color: '#B0BEC5'
              }}>
                <i className="bi bi-lock fs-1"></i>
              </div>
            </div>
            <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Accès restreint</h5>
            <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
              Seuls les gestionnaires et contrôleurs peuvent accéder à cette page.
            </p>
            <button
              className="btn fw-semibold mt-4"
              onClick={() => navigate("/dashboard")}
              style={{
                color: COLORS.primary,
                borderColor: COLORS.primary,
                backgroundColor: 'transparent',
                borderRadius: '8px',
                padding: '10px 24px',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = COLORS.primary;
                e.target.style.color = COLORS.white;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = COLORS.primary;
              }}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Retour au tableau de bord
            </button>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Gestion des sites</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Sites</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-buildings"></i>
                    </div>
                    Liste des sites
                    {userInfo.societe_id && (
                      <span className="badge fw-semibold ms-3" style={{ 
                        backgroundColor: '#E3F2FD',
                        color: COLORS.secondary,
                        padding: '6px 12px',
                        borderRadius: '6px',
                        fontSize: '0.8rem'
                      }}>
                        
                      </span>
                    )}
                  </div>
                </h5>
                
              </div>
              
              {/* Bouton Ajouter site - seulement pour gestionnaires */}
              {isGestionnaire && (
                <button
                  onClick={() => navigate("/sites/create")}
                  className="btn fw-semibold"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i>
                  Ajouter site
                </button>
              )}
            </div>

            {/* Filtres */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <form onSubmit={handleSearch} className="row g-3">
                      {/* Recherche principale */}
                      <div className="col-md-4">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher (nom, adresse, client)..."
                            value={filters.search}
                            onChange={(e) => handleFilterChange("search", e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                        </div>
                      </div>

                      {/* Filtre client */}
                      <div className="col-md-3">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-person"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filters.id_client}
                            onChange={(e) => handleFilterChange("id_client", e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            <option value="">Tous les clients</option>
                            {clients.map(client => (
                              <option key={client.id_client} value={client.id_client}>
                                {client.nom_client} {client.prenom_client}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Filtre statut */}
                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-funnel"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filters.statut_site}
                            onChange={(e) => handleFilterChange("statut_site", e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {STATUT_SITE_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Filtre supprimés */}
                      <div className="col-md-2">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-trash"></i>
                          </span>
                          <select
                            className="form-select"
                            value={filters.supprimer}
                            onChange={(e) => handleFilterChange("supprimer", e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          >
                            {STATUT_SUPPRIME_OPTIONS.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Bouton Filtrer */}
                      <div className="col-md-1">
                        <button 
                          type="submit" 
                          className="btn fw-semibold w-100"
                          style={{
                            backgroundColor: COLORS.primary,
                            borderColor: COLORS.primary,
                            color: COLORS.white,
                            borderRadius: '8px',
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <i className="bi bi-search"></i>
                        </button>
                      </div>
                    </form>

                    {/* Bouton Effacer filtres */}
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-sm fw-semibold"
                        onClick={clearFilters}
                        style={{
                          color: COLORS.primary,
                          borderColor: COLORS.primary,
                          backgroundColor: 'transparent',
                          borderRadius: '6px',
                          padding: '6px 16px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#E3F2FD';
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-x-lg me-1"></i> Effacer les filtres
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
                <div className="mt-3">
                  <button 
                    className="btn btn-sm fw-semibold"
                    onClick={() => fetchSites()}
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '6px',
                      padding: '6px 16px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.primary;
                      e.target.style.color = COLORS.white;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i> 
                    Réessayer
                  </button>
                </div>
              </div>
            )}
            
            {infoMsg && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des sites...</p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr style={{ 
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `2px solid ${COLORS.accent}`
                      }}>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Site</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Client</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Adresse</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Coordonnées</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Statut</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Créé le</th>
                        <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sites.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center py-5">
                            <div className="mb-4">
                              <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                                width: '80px',
                                height: '80px',
                                backgroundColor: '#F5F7FA',
                                color: '#B0BEC5'
                              }}>
                                <i className="bi bi-buildings fs-1"></i>
                              </div>
                            </div>
                            <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun site trouvé</h6>
                            <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                              Aucun site ne correspond à vos critères de recherche.
                            </p>
                            {isGestionnaire && (
                              <button
                                onClick={() => navigate("/sites/create")}
                                className="btn fw-semibold mt-3"
                                style={{
                                  color: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  backgroundColor: 'transparent',
                                  borderRadius: '8px',
                                  padding: '10px 24px',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = COLORS.primary;
                                  e.target.style.color = COLORS.white;
                                  e.target.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = COLORS.primary;
                                  e.target.style.transform = 'translateY(0)';
                                }}
                              >
                                <i className="bi bi-plus-lg me-2"></i> 
                                Ajouter votre premier site
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : (
                        sites.map((site) => {
                          const statut = statutSiteLabel(site.statut_site);
                          const isDeleted = site.supprimer === 1;

                          return (
                            <tr key={site.id_site} className={isDeleted ? "table-secondary" : ""} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                              <td style={{ padding: '16px' }}>
                                <div className="d-flex align-items-center">
                                  <div className="me-3">
                                    <div 
                                      className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                      style={{ 
                                        width: '44px', 
                                        height: '44px', 
                                        fontSize: '15px',
                                        backgroundColor: '#28a745',
                                        boxShadow: '0 2px 4px rgba(40, 167, 69, 0.1)'
                                      }}
                                      title="Site"
                                    >
                                      <i className="bi bi-building"></i>
                                    </div>
                                  </div>
                                  <div>
                                    <div style={{ 
                                      color: COLORS.primary, 
                                      fontWeight: '500',
                                      fontSize: '0.95rem'
                                    }}>
                                      {site.nom_site}
                                      {isDeleted && (
                                        <span className="badge fw-semibold ms-2" style={{ 
                                          backgroundColor: COLORS.danger,
                                          color: 'white',
                                          padding: '4px 8px',
                                          borderRadius: '4px',
                                          fontSize: '0.7rem'
                                        }}>
                                          Supprimé
                                        </span>
                                      )}
                                    </div>
                                    
                                  </div>
                                </div>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                {site.nom_client ? (
                                  <Link 
                                    to={`/clients/details/${site.id_client}`}
                                    className="text-decoration-none fw-semibold"
                                    style={{ color: COLORS.secondary }}
                                  >
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '20px',
                                        height: '20px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.7rem'
                                      }}>
                                        <i className="bi bi-person"></i>
                                      </div>
                                      {site.nom_client}
                                    </div>
                                  </Link>
                                ) : (
                                  <span className="text-muted small" style={{ fontSize: '0.85rem' }}>-</span>
                                )}
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <small style={{ color: COLORS.darkText }}>
                                  {site.addresse_site || '-'}
                                </small>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                {site.latitude_site && site.longitude_site ? (
                                  <div className="small">
                                    <div className="d-flex align-items-center mb-1">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.6rem'
                                      }}>
                                        <i className="bi bi-geo-alt"></i>
                                      </div>
                                      <span style={{ color: COLORS.darkText }}>Lat: {site.latitude_site.toFixed(6)}</span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                      <div className="rounded-circle d-flex align-items-center justify-content-center me-2" style={{ 
                                        width: '16px',
                                        height: '16px',
                                        backgroundColor: '#E3F2FD',
                                        color: COLORS.secondary,
                                        fontSize: '0.6rem'
                                      }}>
                                        <i className="bi bi-geo-alt"></i>
                                      </div>
                                      <span style={{ color: COLORS.darkText }}>Lng: {site.longitude_site.toFixed(6)}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted small" style={{ fontSize: '0.85rem' }}>-</span>
                                )}
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <span className="badge fw-semibold" style={{ 
                                  backgroundColor: site.statut_site === 1 ? '#e8f5e9' : '#f5f5f5',
                                  color: site.statut_site === 1 ? '#2e7d32' : '#757575',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '0.8rem'
                                }}>
                                  <i className={`bi ${statut.icon} me-1`}></i>
                                  {statut.text}
                                </span>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                  {formatDate(site.created_at)}
                                </small>
                              </td>
                              
                              <td style={{ padding: '16px' }}>
                                <button
                                  onClick={() => navigate(`/sites/details/${site.id_site}`)}
                                  className="btn btn-outline-info fw-semibold"
                                  title="Voir détails"
                                  style={{
                                    color: COLORS.primary,
                                    borderRadius: '6px',
                                    padding: '6px 12px',
                                    fontSize: '0.85rem',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  <i className="bi bi-eye me-1"></i> 
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.total_pages > 1 && (
                  <nav className="mt-4">
                    <ul className="pagination justify-content-center">
                      <li className={`page-item ${!pagination.has_prev ? 'disabled' : ''}`}>
                        <button 
                          className="page-link fw-semibold" 
                          onClick={() => fetchSites(pagination.page - 1)}
                          disabled={!pagination.has_prev}
                          style={{
                            color: !pagination.has_prev ? '#B0BEC5' : COLORS.primary,
                            borderColor: '#e0e0e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 4px'
                          }}
                        >
                          <i className="bi bi-chevron-left me-1"></i> 
                          Précédent
                        </button>
                      </li>
                      
                      {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.total_pages - 2) {
                          pageNum = pagination.total_pages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <li key={pageNum} className="page-item">
                            <button 
                              className="page-link fw-semibold" 
                              onClick={() => fetchSites(pageNum)}
                              style={{
                                backgroundColor: pagination.page === pageNum ? COLORS.primary : 'transparent',
                                borderColor: pagination.page === pageNum ? COLORS.primary : '#e0e0e0',
                                color: pagination.page === pageNum ? COLORS.white : COLORS.primary,
                                padding: '8px 16px',
                                borderRadius: '6px',
                                margin: '0 4px',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (pagination.page !== pageNum) {
                                  e.target.style.backgroundColor = '#E3F2FD';
                                  e.target.style.borderColor = COLORS.secondary;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (pagination.page !== pageNum) {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.borderColor = '#e0e0e0';
                                }
                              }}
                            >
                              {pageNum}
                            </button>
                          </li>
                        );
                      })}
                      
                      <li className={`page-item ${!pagination.has_next ? 'disabled' : ''}`}>
                        <button 
                          className="page-link fw-semibold" 
                          onClick={() => fetchSites(pagination.page + 1)}
                          disabled={!pagination.has_next}
                          style={{
                            color: !pagination.has_next ? '#B0BEC5' : COLORS.primary,
                            borderColor: '#e0e0e0',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            margin: '0 4px'
                          }}
                        >
                          Suivant 
                          <i className="bi bi-chevron-right ms-1"></i>
                        </button>
                      </li>
                    </ul>
                    <div className="text-center mt-3">
                      <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          Page {pagination.page} sur {pagination.total_pages}
                        </span>
                        <span className="mx-2">•</span>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          Total: {pagination.total_items} site(s)
                        </span>
                        <span className="mx-2">•</span>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px',
                          margin: '0 8px'
                        }}>
                          {sites.length} sur cette page
                        </span>
                      </small>
                    </div>
                  </nav>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .btn:focus,
        .form-control:focus,
        .page-link:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .page-link {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
      `}</style>
    </Layout>
  );
};

export default SiteList;